/* eslint-disable no-shadow */
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import styled, { createGlobalStyle } from 'styled-components';
import classnames from 'classnames';
import { FiAlertTriangle, FiCheck, FiDisc, FiX, FiPlay } from 'react-icons/fi';

import {
   toggleReferencePlaying,
   documentKeyDown,
   documentKeyUp,
   toggleSelectedPlaying,
   pianoTouchStart,
   selectChordInfoValue,
} from './actions';
import { loadSoundKit } from '../../shared/actions';
import { getNoteName } from '../../util/getNoteName';
import { ControlsWrapper } from '../../components/ControlsWrapper';
import { getHighlightedClassName } from '../../util/getHighlightedClassName';
import { Button } from '../../components/Button';

const KeyboardInterfaceCssVariableOverrides = createGlobalStyle`
   :root {
      @media (min-width: 500px) and (min-height: 500px) {
         --exercise-bottom-padding: 1.5em;
      }

      @media (min-width: 500px) and (min-height: 750px) {
         --exercise-bottom-padding: 2.5em;
      }
   }
`;

const KeyboardInterfaceWrapper = styled.div`
   @media (min-width: 1150px) {
      display: flex;
      justify-content: center;
   }
`;

const KeyboardWrapper = styled.div`
   --keyboard-zone-height: calc(
      var(--visible-and-safe-height) - 2 * var(--header-height) -
         var(--exercise-bottom-padding) - var(--below-interface-height)
   );

   @media (max-height: 550px) {
      --keyboard-zone-height: calc(
         var(--visible-and-safe-height) - var(--header-height) -
            var(--exercise-bottom-padding) - var(--below-interface-height)
      );
   }

   --key-width: min(calc((var(--safe-width) - 8px) / 7), 80px);
   --key-border-radius: 10px;
   --relative-key-height: calc(4 * var(--key-width));

   --screen-key-height: calc((var(--keyboard-zone-height) - 10px) / 2);

   --key-height: min(var(--relative-key-height), var(--screen-key-height));

   touch-action: none;
   -webkit-tap-highlight-color: transparent;

   @media (min-width: 500px) {
      --key-width: min(calc((var(--safe-width) + 6px) / 14), 80px);

      --screen-key-height: calc(var(--keyboard-zone-height));
   }

   display: inline-block;
   width: calc(var(--safe-width) - 10px);

   @media (prefers-color-scheme: dark) {
      & {
         --background-color: var(--brand-light-green);
         --text-color: var(--brand-darker-green);
      }
   }

   @media (min-width: 1150px) {
      width: auto;
   }

   button {
      --key-color: var(--background-color);
      border-radius: 0 0 var(--key-border-radius) var(--key-border-radius);
      position: relative;
      color: var(--text-color);
      background-color: var(--key-color);
      border: 2px solid;
      margin-left: -2px;
      display: inline-flex;
      align-items: flex-end;
      justify-content: center;
      padding-bottom: 0.5em;
      @media (min-height: 350px) {
         padding-bottom: 1.3em;
      }
      vertical-align: top;
      width: var(--key-width);
      height: var(--key-height);
      outline: 0;
      cursor: pointer;
      transition: background-color 0.25s ease;
      transform-origin: top;

      &:hover {
         border-color: var(--brand-dark-orange) !important;
         transform: scale(1.0125);
      }

      &.pressed,
      &[data-note-name*='#'].pressed {
         --key-color: var(--brand-light-green);
         color: var(--text-color);
      }

      &:first-child {
         margin-left: 0;
         border-top-left-radius: var(--key-border-radius);
      }

      &:last-child {
         border-top-right-radius: var(--key-border-radius);
      }

      .key-icon-wrapper {
         pointer-events: none;
         opacity: 0;
         transition: opacity 0.25s ease;

         svg {
            width: 20px;

            @media (min-width: 350px) {
               width: 25px;
            }

            @media (min-width: 450px) {
               width: auto;
            }

            @media (min-width: 500px) {
               width: 20px;
            }

            @media (min-width: 650px) {
               width: 25px;
            }

            @media (min-width: 850px) {
               width: auto;
            }
         }
      }

      .key-label {
         background-color: var(--key-color);
         transition: background-color 0.25s ease;
         padding: 3px;
         border-radius: 5px;
         font-weight: bold;

         font-family: var(--content-font);

         @media (min-width: 650px) {
            font-size: 1.5em;
         }
      }
   }

   @media (max-width: 499px) {
      button:nth-child(12) {
         border-top-right-radius: var(--key-border-radius);
      }
      button:nth-child(13) {
         border-top-left-radius: var(--key-border-radius);
      }

      button:first-child {
         margin-bottom: 10px;
      }
   }

   &&&:not(.compare-answer.reference-playing) {
      button:not([data-note-name*='#']) {
         &.selected {
            z-index: 1;
         }

         &:hover,
         &.pressed,
         &.highlighted,
         &.highlighted-once,
         &.highlighted-once-b {
            z-index: 2;
         }
      }
   }

   &&.compare-answer {
      .reference:not([data-note-name*='#']) {
         z-index: 1;
      }
   }

   button[data-note-name*='#'] {
      --sharp-key-width: calc(var(--key-width) / 1.75);
      --key-color: var(--text-color);
      color: var(--background-color);
      border-color: var(--text-color);
      margin: 0 calc(-1 * var(--sharp-key-width) / 3) 0
         calc(-2 * var(--sharp-key-width) / 3);
      z-index: 3;

      width: var(--sharp-key-width);
      height: calc(0.67 * var(--key-height));

      &:first-child {
         margin-left: 0;
      }
   }

   &:not(.compare-answer.reference-playing) {
      .selected,
      [data-note-name*='#'].selected {
         --key-color: var(--brand-orange);
         color: var(--brand-darker-orange);
         border-color: var(--brand-darker-orange);
      }

      .selected.pressed,
      [data-note-name*='#'].selected.pressed {
         --key-color: var(--brand-light-orange);
         color: var(--brand-darker-orange);
         border-color: var(--brand-dark-orange);
      }

      .selected .key-icon-wrapper {
         opacity: 1;
      }
   }

   &&.compare-answer {
      &:not(.reference-playing) {
         .selected,
         .selected[data-note-name*='#'] {
            --key-color: var(--semantic-error);
            color: var(--semantic-error-text);
            border-color: var(--semantic-error-text);
         }
         .selected.pressed,
         .selected.pressed[data-note-name*='#'] {
            --key-color: var(--semantic-error-interact);
            border-color: var(--semantic-error);
         }
      }

      &:not(.selected-playing) {
         .reference,
         .reference[data-note-name*='#'] {
            --key-color: var(--semantic-warn);
            color: var(--semantic-warn-text);
            border-color: var(--semantic-warn-text);
         }
         .reference.pressed,
         .reference.pressed[data-note-name*='#'] {
            --key-color: var(--semantic-warn-interact);
            border-color: var(--semantic-warn);
         }
      }

      .selected.reference,
      .selected.reference[data-note-name*='#'] {
         --key-color: var(--semantic-success);
         color: var(--semantic-success-text);
         border-color: var(--semantic-success-text);
      }
      .selected.reference.pressed,
      .selected.reference.pressed[data-note-name*='#'] {
         --key-color: var(--semantic-success-interact);
         border-color: var(--semantic-success);
      }
   }

   .selected-icon,
   .missing-icon,
   .incorrect-icon,
   .correct-icon,
   .key-label {
      display: none;
   }

   .selected:not(.labeled) .selected-icon,
   .labeled .key-label {
      display: block;
   }

   .labeled .key-icon-wrapper {
      opacity: 1;
   }

   &.compare-answer {
      .selected .selected-icon {
         display: none;
      }

      &:not(.reference-playing)
         .selected:not(.reference):not(.labeled)
         .incorrect-icon,
      &:not(.selected-playing)
         .reference:not(.selected):not(.labeled)
         .missing-icon,
      .reference.selected:not(.labeled) .correct-icon {
         display: block;
      }

      &:not(.reference-playing) .selected:not(.reference) .key-icon-wrapper,
      &:not(.selected-playing) .reference:not(.selected) .key-icon-wrapper,
      .reference.selected .key-icon-wrapper {
         opacity: 1;
      }

      .selected .key-label {
         font-weight: normal;
      }
   }

   @media (prefers-color-scheme: dark) {
      button {
         &.pressed,
         &[data-note-name*='#'].pressed {
            --key-color: var(--brand-green);
            color: var(--brand-dark-green);
         }
      }
   }
`;

const NameSelectionWrapper = styled.div`
   font-family: var(--content-font);

   h1 {
      font-family: var(--heading-font);
      letter-spacing: 0.025em;
      line-height: 1.42;
      font-weight: normal;
      margin-top: 1.5em;
      margin-bottom: 1em;
   }

   select {
      appearance: none;
      background-color: var(--background-color);
      border: none;
      padding: 0;
      margin: 0;
      font-family: inherit;
      font-size: 1.25em;
      cursor: inherit;
      line-height: 1.2;
      color: var(--text-color);
      outline: none;

      border: 2px solid;
      border-radius: 0.25em;
      padding: 0.5em 1.5em 0.5em 0.5em;
      margin: 0 0.5em 0.5em 0;
      cursor: pointer;
      background-image: url("data:image/svg+xml;utf8,<svg stroke='%23006061' fill='none' stroke-width='2' viewBox='0 0 24 24' stroke-linecap='round' stroke-linejoin='round' xmlns='http://www.w3.org/2000/svg'><polyline points='6 9 12 15 18 9'></polyline></svg>");
      background-size: 1.25em;
      background-position: right 3px center;
      background-repeat: no-repeat;

      &:hover,
      &:focus,
      &:active {
         color: var(--hover-color);
         border-color: var(--hover-color);
         background-image: url("data:image/svg+xml;utf8,<svg stroke='%23ff6912' fill='none' stroke-width='2' viewBox='0 0 24 24' stroke-linecap='round' stroke-linejoin='round' xmlns='http://www.w3.org/2000/svg'><polyline points='6 9 12 15 18 9'></polyline></svg>");
      }

      &:disabled {
         opacity: 1;
         pointer-events: none;
      }
   }

   @media (prefers-color-scheme: dark) {
      select {
         background-image: url("data:image/svg+xml;utf8,<svg stroke='%2380bfbf' fill='none' stroke-width='2' viewBox='0 0 24 24' stroke-linecap='round' stroke-linejoin='round' xmlns='http://www.w3.org/2000/svg'><polyline points='6 9 12 15 18 9'></polyline></svg>");
      }
   }

   @media (prefers-color-scheme: light) {
      select {
         &:hover,
         &:focus,
         &:active {
            background-color: var(--hover-color);
            color: var(--hover-text-color);
            border-color: var(--brand-darker-orange);
            background-image: url("data:image/svg+xml;utf8,<svg stroke='%23ffffff' fill='none' stroke-width='2' viewBox='0 0 24 24' stroke-linecap='round' stroke-linejoin='round' xmlns='http://www.w3.org/2000/svg'><polyline points='6 9 12 15 18 9'></polyline></svg>");
         }
      }
   }

   &.compare-answer {
      .correct {
         color: var(--semantic-success-text);
         background-color: var(--semantic-success);
         background-image: url("data:image/svg+xml;utf8,<svg stroke='%23005229' fill='none' stroke-width='2' viewBox='0 0 24 24' stroke-linecap='round' stroke-linejoin='round' xmlns='http://www.w3.org/2000/svg'><polyline points='20 6 9 17 4 12'></polyline></svg>");
      }
      .incorrect {
         color: var(--semantic-error-text);
         background-color: var(--semantic-error);
         background-image: url("data:image/svg+xml;utf8,<svg stroke='%235a0c13' fill='none' stroke-width='2' viewBox='0 0 24 24' stroke-linecap='round' stroke-linejoin='round' xmlns='http://www.w3.org/2000/svg'><line x1='18' y1='6' x2='6' y2='18'></line><line x1='6' y1='6' x2='18' y2='18'></line></svg>");
      }
   }
`;

const NotAChord = styled.div`
   font-family: var(--content-font);
   text-align: center;
   color: var(--hover-color);
`;

const mapStateToProps = ({ activeInterface }) => ({
   activeInterface,
});

const mapDispatchToProps = {
   toggleReferencePlaying,
   toggleSelectedPlaying,
   documentKeyDown,
   documentKeyUp,
   loadSoundKit,
   pianoTouchStart,
   selectChordInfoValue,
};

const KeyboardInterface = connect(
   mapStateToProps,
   mapDispatchToProps
)(
   ({
      activeInterface,
      toggleReferencePlaying,
      toggleSelectedPlaying,
      documentKeyDown,
      documentKeyUp,
      loadSoundKit,
      pianoTouchStart,
      selectChordInfoValue,
   }) => {
      useEffect(() => {
         loadSoundKit({
            soundKit: {
               name: 'Just Keys',
               sounds: {
                  keys: { presetId: 'default-harp' },
               },
            },
         });
      }, [loadSoundKit]);

      useEffect(() => {
         const keydownHandler = (e) => {
            if (!e.target.matches('input')) {
               documentKeyDown(e.code);
            }
         };
         const keyupHandler = (e) => {
            if (!e.target.matches('input')) {
               documentKeyUp(e.code);
            }
         };
         document.addEventListener('keydown', keydownHandler);
         document.addEventListener('keyup', keyupHandler);

         return () => {
            document.removeEventListener('keydown', keydownHandler);
            document.removeEventListener('keyup', keyupHandler);
         };
      }, [documentKeyUp, documentKeyDown]);

      return (
         <KeyboardInterfaceWrapper>
            <KeyboardInterfaceCssVariableOverrides />
            <div>
               <ControlsWrapper>
                  <div>
                     <Button
                        type="button"
                        onClick={toggleReferencePlaying}
                        className={classnames('button-icon icon-with-text', {
                           highlighted:
                              activeInterface.highlightedControls.playReference,
                           hidden: activeInterface.hiddenControls.playReference,
                        })}
                     >
                        {activeInterface.referencePlaying ? (
                           <svg
                              stroke="currentColor"
                              fill="none"
                              strokeWidth="2"
                              viewBox="0 0 24 24"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              height="24"
                              width="24"
                              xmlns="http://www.w3.org/2000/svg"
                           >
                              <rect x="4" y="4" width="16" height="16" />
                           </svg>
                        ) : (
                           <FiPlay size="24" />
                        )}{' '}
                        <span>Reference</span>
                     </Button>

                     {(activeInterface.compareAnswer ||
                        activeInterface.canPlaySelectedWithoutCompareAnswer) && (
                        <Button
                           type="button"
                           onClick={toggleSelectedPlaying}
                           className={classnames('button-icon icon-with-text', {
                              highlighted:
                                 activeInterface.highlightedControls
                                    .playSelected,
                           })}
                        >
                           {activeInterface.selectedPlaying ? (
                              <svg
                                 stroke="currentColor"
                                 fill="none"
                                 strokeWidth="2"
                                 viewBox="0 0 24 24"
                                 strokeLinecap="round"
                                 strokeLinejoin="round"
                                 height="24"
                                 width="24"
                                 xmlns="http://www.w3.org/2000/svg"
                              >
                                 <rect x="4" y="4" width="16" height="16" />
                              </svg>
                           ) : (
                              <FiPlay size="24" />
                           )}{' '}
                           <span>Selected</span>
                        </Button>
                     )}
                  </div>
               </ControlsWrapper>
               {activeInterface.view === 'keyboard' && (
                  <KeyboardWrapper
                     className={classnames({
                        'compare-answer': activeInterface.compareAnswer,
                        'reference-playing': activeInterface.referencePlaying,
                        'selected-playing': activeInterface.selectedPlaying,
                     })}
                     onPointerDown={(e) =>
                        pianoTouchStart(e.clientX, e.clientY, e.button)
                     }
                     onContextMenu={(e) => e.preventDefault()}
                  >
                     {activeInterface.notes.map((note) => {
                        const noteName = getNoteName(note);
                        return (
                           <button
                              key={note}
                              type="button"
                              data-note-name={noteName}
                              data-note={note}
                              className={classnames('keyboard-key', {
                                 selected: activeInterface.selectedNotes[note],
                                 reference:
                                    activeInterface.referenceNotes[note],
                                 pressed: activeInterface.pressedNotes[note],
                                 labeled:
                                    activeInterface.labeledNotes[note] != null,
                                 [getHighlightedClassName(
                                    activeInterface.highlightedControls[
                                       `note:${note}`
                                    ]
                                 )]:
                                    !!activeInterface.highlightedControls[
                                       `note:${note}`
                                    ],
                              })}
                           >
                              <span className="key-icon-wrapper">
                                 <span className="key-label">
                                    {activeInterface.labeledNotes[note]}
                                 </span>
                                 <FiAlertTriangle
                                    size="32"
                                    className="missing-icon"
                                 />
                                 <FiCheck size="32" className="correct-icon" />
                                 <FiX size="32" className="incorrect-icon" />
                                 <FiDisc size="32" className="selected-icon" />
                              </span>
                           </button>
                        );
                     })}
                  </KeyboardWrapper>
               )}

               {activeInterface.view === 'nameSelect' && (
                  <NameSelectionWrapper
                     className={classnames({
                        'compare-answer': activeInterface.compareAnswer,
                        'reference-playing': activeInterface.referencePlaying,
                        'selected-playing': activeInterface.selectedPlaying,
                     })}
                  >
                     <h1>Name the Chord You Hear:</h1>
                     {activeInterface.chordInfo.inversion && (
                        <NameSelect
                           onChange={(value) =>
                              selectChordInfoValue('inversion', value)
                           }
                           parameter={activeInterface.chordInfo.inversion}
                           compareAnswer={activeInterface.compareAnswer}
                           selectedPlaying={activeInterface.selectedPlaying}
                        />
                     )}
                     {activeInterface.chordInfo.chordName && (
                        <NameSelect
                           onChange={(value) =>
                              selectChordInfoValue('chordName', value)
                           }
                           parameter={activeInterface.chordInfo.chordName}
                           compareAnswer={activeInterface.compareAnswer}
                           selectedPlaying={activeInterface.selectedPlaying}
                        />
                     )}
                     {activeInterface.chordInfo.type && (
                        <NameSelect
                           onChange={(value) =>
                              selectChordInfoValue('type', value)
                           }
                           parameter={activeInterface.chordInfo.type}
                           compareAnswer={activeInterface.compareAnswer}
                           selectedPlaying={activeInterface.selectedPlaying}
                        />
                     )}
                     {activeInterface.chordInfo.voicing && (
                        <NameSelect
                           onChange={(value) =>
                              selectChordInfoValue('voicing', value)
                           }
                           parameter={activeInterface.chordInfo.voicing}
                           compareAnswer={activeInterface.compareAnswer}
                           selectedPlaying={activeInterface.selectedPlaying}
                        />
                     )}
                  </NameSelectionWrapper>
               )}
               {activeInterface.view === 'nameSelect' &&
                  activeInterface.compareAnswer &&
                  activeInterface.selectedPlaying &&
                  Object.keys(activeInterface.selectedNotes).length == 0 && (
                     <NotAChord>Not a chord</NotAChord>
                  )}
            </div>
         </KeyboardInterfaceWrapper>
      );
   }
);

function NameSelect({ parameter, onChange, compareAnswer, selectedPlaying }) {
   return (
      <select
         disabled={compareAnswer}
         className={
            parameter.selected === parameter.value ? 'correct' : 'incorrect'
         }
         value={
            compareAnswer && !selectedPlaying
               ? parameter.value
               : parameter.selected
         }
         onChange={(e) => onChange(e.target.value)}
      >
         <option value="" disabled>
            {parameter.label}
         </option>
         {parameter.options.map((o) => (
            <option value={o} key={o}>
               {o}
            </option>
         ))}
      </select>
   );
}

export { KeyboardInterface };
